import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import {
  InputGroupPropsTypes,
  OrganizationData,
} from "../inputComp/InputGroupType";
import {
  AddressIcon,
  CloseIcon,
  DepartmentIcon,
  EmailIcon,
  FaxIcon,
  IdIcon,
  MobileIcon,
  NameIcon,
  OrganizationIcon,
  SearchIcon,
  UserIcon,
} from "../iconSet/IconSet";
import InputGroup from "../inputComp/InputGroup";
import LoadingScreen from "../loadingScreenComp/LoadingScreen";
import axios from "axios";
import AlertDialog from "../popupComp/AlertDialog";

import loadLightIcon from '../../images/icons/load-light.gif'
import loadDarkIcon from '../../images/icons/load-dark.gif'
import { useTheme } from "../../context/ThemeContext";

interface Organization {
  organizationid: string;
  organizationname: string;
  shortname: string;
  email: string;
  address: string;
  telephonenumber: string;
  faxnumber: string;
  numberofemployees: string;
}

const CreateOrganization = () => {
  // Loading screen
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { theme } = useTheme()

  const [alertDialogState, setAlertDialogState] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');
  const [alertDescription, setAlertDescription] = useState('');
  const [alertCallback, setAlertCallback] = useState<() => void | undefined>();

  const showAlertDialog = (title: string, description: string, callback?: () => void) => {
  setAlertTitle(title);
  setAlertDescription(description);
  setAlertCallback(() => callback); // wrap in function to preserve reference
  setAlertDialogState(true);
};

  const [isDataPickerOpen, setIsDataPickerOpen] = useState<boolean>(false);
  const [isDataPickerLoading, setIsDataPickerLoading] = useState<boolean>(true);

  const allOrganizations = useRef<OrganizationData[]>([]);
  const [filteredOrganizationData, setFilteredOrganizationData] = useState<
    OrganizationData[]
  >([]);

  const idRef = useRef<HTMLInputElement | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);
  const shortNameRef = useRef<HTMLInputElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const addressRef = useRef<HTMLInputElement | null>(null);
  const telephoneNumberRef = useRef<HTMLInputElement | null>(null);
  const faxNumberRef = useRef<HTMLInputElement | null>(null);
  //const headOfTheOrganizationRef = useRef<HTMLInputElement | null>(null);
  //const employeesNumberRef = useRef<HTMLInputElement | null>(null);
  //const departmentRef = useRef<HTMLInputElement | null>(null);

  const [refreshKey, setRefreshKey] = useState(0);

  const BASE_URL = process.env.REACT_APP_BASE_URL

  const [form, setForm] = useState<Organization>({
    organizationid: "",
    organizationname: "",
    shortname: "",
    email: "",
    address: "",
    telephonenumber: "",
    faxnumber: "",
    numberofemployees: "",
  });

  const [isUpdate, setIsUpdate] = useState(false);

  const handleChange = (e: any) => {
    console.log(e.target.name, e.target.value);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleBlur = async () => {
    const token = localStorage.getItem("access_token");
    if (form.organizationid.trim() !== "") {
      try {
        //const res = await axios.get<Organization>(`https://bos.beconx.live/api/v1/getorganizationbyid/${form.organizationid}`,
        const res = await axios.get<Organization>(BASE_URL + `/identity_manager/api/v1/getorganizationbyid/${form.organizationid}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (res.data) {
          setForm(res.data);
          console.log(form);
          setIsUpdate(true);
        } else {
          setForm({
            organizationid: form.organizationid,
            organizationname: "",
            shortname: "",
            email: "",
            address: "",
            telephonenumber: "",
            faxnumber: "",
            numberofemployees: "",
          });
          setIsUpdate(false);
        }
      } catch {
        setIsUpdate(false);
      }
    }
  };

  // existing data change handle
  const handleChangeExistingDataSearch = (
    event: ChangeEvent<HTMLInputElement>
  ): void => {
    if (event.target.value === "") {
      setFilteredOrganizationData(allOrganizations.current);
    } else {
      setFilteredOrganizationData(
        allOrganizations.current.filter((item) =>
          item.organizationname.toLowerCase().includes(event.target.value.toLowerCase()) ||
          item.shortname.toLowerCase().includes(event.target.value.toLowerCase()) ||
          item.email.toLowerCase().includes(event.target.value.toLowerCase())
        )
      );
    }
  };

  // Handle clicking filtered item
  const handleClickFilteredOrganization = (
    organization: OrganizationData
  ): void => {
    if (
      idRef.current &&
      nameRef.current &&
      shortNameRef.current &&
      emailRef.current &&
      addressRef.current &&
      telephoneNumberRef.current &&
      faxNumberRef.current //&&
      //employeesNumberRef.current
    ) {
      idRef.current.value = organization.organizationid.toString();
      nameRef.current.value = organization.organizationname.toString();
      shortNameRef.current.value = organization.shortname.toString();
      emailRef.current.value = organization.email.toString();
      addressRef.current.value = organization.address.toString();
      telephoneNumberRef.current.value =
        organization.telephonenumber.toString();
      faxNumberRef.current.value = organization.faxnumber.toString();
      //employeesNumberRef.current.value = organization.numberofemployees.toString();
    }
    // Update form state
    setForm({
      organizationid: organization.organizationid.toString(),
      organizationname: organization.organizationname.toString(),
      shortname: organization.shortname.toString(),
      email: organization.email.toString(),
      address: organization.address.toString(),
      telephonenumber: organization.telephonenumber.toString(),
      faxnumber: organization.faxnumber.toString(),
      numberofemployees: organization.numberofemployees.toString(),
    });
    setIsDataPickerOpen(false);
    setIsUpdate(true);
  };

  // Form items
  const formItems: InputGroupPropsTypes[] = [
    {
      type: "text",
      icon: <IdIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization ID",
      label: "Organization Id",
      name: "organizationid",
      componentInputElement: idRef,
      onChange: handleChange,
      value: form.organizationid,
      onBlur: handleBlur,
      dataPicker: true,
      isDataPickerOpen: isDataPickerOpen,
      setIsDataPickerOpen: setIsDataPickerOpen,
      required: true
    },
    {
      type: "text",
      icon: <OrganizationIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization name",
      label: "Organization Name",
      name: "organizationname",
      componentInputElement: nameRef,
      onChange: handleChange,
      value: form.organizationname,
      required: true
    },
    {
      type: "text",
      icon: <NameIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization short name",
      label: "Organization Short Name",
      name: "shortname",
      componentInputElement: shortNameRef,
      onChange: handleChange,
      value: form.shortname,
      required: true
    },
    {
      type: "text",
      icon: <EmailIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization email",
      label: "Organization Email",
      name: "email",
      componentInputElement: emailRef,
      onChange: handleChange,
      value: form.email,
      required: true
    },
    {
      type: "text",
      icon: <AddressIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization address",
      label: "Organization Address",
      name: "address",
      componentInputElement: addressRef,
      onChange: handleChange,
      value: form.address,
    },
    {
      type: "text",
      icon: <MobileIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization telephone number",
      label: "Organization Telephone Number",
      name: "telephonenumber",
      componentInputElement: telephoneNumberRef,
      onChange: handleChange,
      value: form.telephonenumber,
    },
    {
      type: "text",
      icon: <FaxIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization fax number",
      label: "Organization Fax Number",
      name: "faxnumber",
      componentInputElement: faxNumberRef,
      onChange: handleChange,
      value: form.faxnumber,
    },
    /* {
      type: "text",
      icon: <UserIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter organization head",
      label: "organization head",
      name: "organizationHead",
      componentInputElement: headOfTheOrganizationRef,
      onChange: handleChange,
      onBlur: handleBlur,
    }, */
    // {
    //   type: "text",
    //   icon: <UserIcon width="1.5rem" color="var(--color-main-white)" />,
    //   color: "var(--color-accent-bg)",
    //   placeholder: "Enter number of employees",
    //   label: "Number of employees",
    //   name: "numberofemployees",
    //   componentInputElement: employeesNumberRef,
    //   //min: 0,
    //   onChange: handleChange,
    //   value: form.numberofemployees,
    // },
    /* {
      type: "text",
      icon: <DepartmentIcon width="1.5rem" color="var(--color-main-white)" />,
      color: "var(--color-accent-bg)",
      placeholder: "Enter department",
      label: "department",
      name: "departments",
      componentInputElement: departmentRef,
      onChange: handleChange,
      //onBlur: handleBlur,
      value: form.departments
    }, */
  ];

  const handleCreateOrganization = async () => {
    setIsLoading(true)
    const token = localStorage.getItem("access_token");
    if (!form.organizationid || !form.organizationname) {
      showAlertDialog("Missing Fields", "ID and name are required.");
      setIsLoading(false)
      return;
    }

    //await axios.post("https://bos.beconx.live/api/v1/createorganization", form, {
    await axios.post(BASE_URL + "/identity_manager/api/v1/createorganization", form, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    showAlertDialog(isUpdate ? "Success" : "Created", isUpdate ? "Organization updated." : "Organization created.");

    setForm({
      organizationid: "",
      organizationname: "",
      shortname: "",
      email: "",
      address: "",
      telephonenumber: "",
      faxnumber: "",
      numberofemployees: "",
    });
    setIsUpdate(false);
    setIsLoading(false)
	setRefreshKey((prev) => prev + 1);
  };

  const handleDeleteOrganization = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      showAlertDialog("Unauthorized", "Missing access token.");
      return;
    }

    try {
      setIsLoading(true)
      //const response = await fetch(`https://bos.beconx.live/api/v1/organizationdelete/${form.organizationid}`,
      const response = await fetch(BASE_URL + `/identity_manager/api/v1/organizationdelete/${form.organizationid}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const resultText = await response.text(); // assuming plain string response like "User deleted successfully"
        showAlertDialog("Deleted", resultText);
        setForm({
          organizationid: "",
          organizationname: "",
          shortname: "",
          email: "",
          address: "",
          telephonenumber: "",
          faxnumber: "",
          numberofemployees: "",
        });
		setIsUpdate(false);
      } else {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const error = await response.json();
          console.log(error.message || "Error deleting user");
        } else {
          const errorText = await response.text();
          console.log(errorText);
        }
      }
    } catch (err) {
      console.error("Delete failed", err);
      console.log("An error occurred during deletion");
    }
    finally{
      setIsLoading(false)
    }
  };

  // Get all available organization
  const getAllOrganizations = () => {
    setIsDataPickerLoading(true)
    const token = localStorage.getItem("access_token");
    //fetch("https://bos.beconx.live/api/v1/getallorganizations", {
    fetch(BASE_URL + "/identity_manager/api/v1/getallorganizations", {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => res.json())
      .then((data) => {
        //console.log("Fetched organizations in Create Org:", data);
		allOrganizations.current = data
        setFilteredOrganizationData(data);
      })
      .catch((err) => console.error("Error fetching organizations:", err))
      .finally(() => {
        setIsDataPickerLoading(false)
      })
    //allOrganizations.current = data
    //setFilteredOrganizationData(data)
  };

  useEffect(() => {
    getAllOrganizations();
  }, [isUpdate,refreshKey]);

  return (
    <div className={`container-wrapper`}>
      <span className={`title`}>setup organization</span>

      <div className={`create-organization`}>
        {/* Early warning -> workflow diagram -> create department */}
        <div className={`grid-layout-create-organization`}>
          <div className={`left-side`}>
            {formItems.slice(0, 5).map((field, index) => (
              <InputGroup key={index} {...field} />
            ))}
          </div>

          <div className={`right-side`}>
            {formItems.slice(5).map((field, index) => (
              <InputGroup key={index} {...field} />
            ))}
          </div>

          <div className={`button-wrapper`} style={{ gridArea: "button" }}>
            <div className={`left-side`}></div>
            <div className={`right-side`}>
              <button
                className={`button-normal`}
                onClick={(e) => {
                  showAlertDialog(
                      "Confirm Submit",
                      "Are you sure you want to submit this organization data?",
                      handleCreateOrganization
                  )
                }}
              >
                {isUpdate ? "Update" : "Create"}
              </button>
              {isUpdate && (
                <button
                  className={`button-normal`}
                  style={{ backgroundColor: "var(--color-main-red)" }}
                  onClick={(e) => 
                    showAlertDialog(
                      "Confirm Delete",
                      "Are you sure you want to delete this organization?",
                      handleDeleteOrganization
                    )
                  }
                >
                  Delete
                </button>
              )}
            </div>
          </div>
          {isDataPickerOpen && (
            <div className="data-picker-table-wrapper">
              <div className="data-picker-table-container">
                <div className="search-wrapper">
                  <input
                    type="text"
                    placeholder="Search Organization"
                    onChange={(e) => handleChangeExistingDataSearch(e)}
                  />
                  <div className="search-icon">
                    <SearchIcon width="1rem" color="var(--color-main-white)" />
                  </div>
                  <div
                    className="close-icon"
                    onClick={(e) => setIsDataPickerOpen(false)}
                  >
                    <CloseIcon width="1.5rem" color="var(--color-text)" />
                  </div>
                </div>
                <div className="row header">
                    <span style={{ width: '33%' }}>Organization</span>
                    <span style={{ width: '33%' }}>Short Name</span>
                    <span style={{ width: '33%' }}>Email</span>
                </div>

                {isDataPickerLoading ? (
										<div className="searching-data">
											<img src={theme === 'dark' ? loadDarkIcon : theme === 'light' ? loadLightIcon : theme === 'colorful' ? loadLightIcon : ''} alt="" />
											<span>Searching user data...</span>
										</div>
									) : (
										<div className="data-wrapper">
                      {filteredOrganizationData.map((organization, index) => (
                        <div
                          key={index}
                          className="row"
                          onClick={(e) =>
                            handleClickFilteredOrganization(organization)
                          }
                        >
                          <span style={{width: '33%'}}>{organization.organizationname}</span>
                          <span style={{width: '33%'}}>{organization.shortname}</span>
                          <span style={{width: '33%'}}>{organization.email}</span>
                        </div>
                      ))}
                    </div>
									)}
                
              </div>
            </div>
          )}
          
          {alertDialogState && (
            <AlertDialog
              title={alertTitle}
              description={alertDescription}
              windowState={alertDialogState}
              setWindowState={setAlertDialogState}
              function={alertCallback}
            />
          )}
        </div>
      </div>
      {isLoading ? <LoadingScreen /> : null}
    </div>
  );
};

export default CreateOrganization;
